import { Element, useEditor, useNode } from "@craftjs/core";
import { Popover } from "@headlessui/react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { Schedule } from "src/types";

import AddToCalendar from "@/components/add-to-calendar/add-to-calendar-component";
import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import Text from "@/components/editor/primitives/text/text-component";
import { RenderHTMLContent } from "@/components/editor/render-html-content/render-html-content-component";
import { BlockMaxWidthWrapper } from "@/components/editor/shared/block-wrapper";
import { AvatarGroup } from "@/components/editor/shared/schedules/avatar-group";
import EmptyItem from "@/components/editor/shared/schedules/empty-item-component";
import Filter from "@/components/editor/shared/schedules/filter-component";
import { Notification } from "@/components/editor/shared/schedules/notification-component";
import PartsTagsDisplay from "@/components/editor/shared/schedules/parts-tags-display-component";
import SyncedScheduleSettings from "@/components/editor/shared/schedules/schedule-settings-component";
import { mapFirstLastNames } from "@/components/editor/shared/schedules/utils";
import { Typography } from "@/components/editor/shared/typography";
import { SearchInput } from "@/components/editor/themes/shared/search-input";
import { CalendarAdd } from "@/components/icons/calendar-add";
import { getGroupedSchedules } from "@/features/schedules/schedules-reducer";
import { useScheduleFilter } from "@/hooks/use-schedule-filter";
import { useScheduleTimezone } from "@/hooks/use-schedule-timezone";
import { useSchedulesWithEventTimezone } from "@/hooks/use-schedules-with-event-timezone";
import useViewportContext from "@/hooks/use-viewport-context";
import getFormattedScheduleTime from "@/utils/dates/getFormattedScheduleTime";

import {
  EmptyItemWrapper,
  FilterWrapper,
  StyledCalendarButton,
  StyledMenuContent,
  StyledScheduleCard,
  StyledScheduleCardBody,
  StyledScheduleCardHeader,
  StyledScheduleName,
  StyledScheduleSegment,
  StyledSchedulesList,
  StyledScheduleTitle,
  StyledSpeakersList,
  Wrapper,
} from "./default-schedule-component-styles";

const ScheduleCard = ({ schedule }) => {
  const { t } = useTranslation();
  const selectedTimezone = useScheduleTimezone();
  const type = schedule.event_part.event_part_type;
  const segment =
    schedule.event_part.event_type_custom_text ??
    type.charAt(0).toUpperCase() + type.slice(1);

  const speakers = mapFirstLastNames(schedule.speakers || []).map(
    (speaker) => ({
      firstName: speaker.first_name,
      lastName: speaker.last_name,
      image: speaker.image_url,
    })
  );

  return (
    <StyledScheduleCard>
      <StyledScheduleCardHeader>
        <Typography pattern="labelOne">
          {getFormattedScheduleTime(
            schedule.time_start,
            schedule.time_end,
            selectedTimezone,
            true
          )}
        </Typography>
        <AddToCalendar
          event={{
            external_id: schedule.external_id,
            time_start: schedule.time_start,
            time_end: schedule.time_end,
            name: schedule.name,
          }}
        >
          <StyledCalendarButton
            title={t("preference-panel.schedule.add-to-calendar")}
          >
            <CalendarAdd className="size-[1.125rem]" />
          </StyledCalendarButton>
        </AddToCalendar>
      </StyledScheduleCardHeader>
      <StyledScheduleCardBody>
        <StyledScheduleSegment>
          <Typography pattern="labelOne">{segment}</Typography>
        </StyledScheduleSegment>
        <StyledScheduleName>
          <Typography pattern="headingFour">{schedule.name}</Typography>
        </StyledScheduleName>
        <div>
          <Typography
            as={RenderHTMLContent}
            pattern="description"
            html={schedule.description}
          />
        </div>
        {speakers.length > 0 && (
          <StyledSpeakersList>
            <AvatarGroup
              popupLabel={t("preference-panel.schedule.additional-speakers")}
              data-testid="speaker-avatars-group"
              users={speakers}
            />
          </StyledSpeakersList>
        )}
      </StyledScheduleCardBody>
    </StyledScheduleCard>
  );
};

export default function SyncedDefaultSchedule(props: {
  schedules?: Schedule[];
}) {
  const {
    connectors: { connect },
    id,
  } = useNode();

  const dashboardSchedule = useSelector(getGroupedSchedules);
  const groupedSchedules = props.schedules ?? dashboardSchedule;

  const isEmpty = groupedSchedules.length === 0;
  const { enabled } = useEditor((state) => ({
    enabled: state.isEnabled(),
  }));

  const { t } = useTranslation();
  const { viewport } = useViewportContext();
  const {
    clearAllSelections,
    filters,
    handlePartSelection,
    handleTagSelection,
    isMenuOpen,
    menuRef,
    schedules,
    searchValue,
    selectedParts,
    selectedTags,
    setSearchValue,
    toggleMenu,
  } = useScheduleFilter(groupedSchedules);

  const schedulesWithEventTimezone = useSchedulesWithEventTimezone(schedules);

  const isMobile = React.useMemo(() => viewport === "mobile", [viewport]);
  const totalActiveFilters = selectedTags.length + selectedParts.length;

  const renderSearchBar = React.useCallback(
    () => (
      <SearchInput
        aria-label={t("schedule.search-segments")}
        label={t("schedule.search-segments")}
        onClear={() => setSearchValue("")}
        onChange={setSearchValue}
        value={searchValue}
      />
    ),
    [searchValue, setSearchValue, t]
  );

  if (!enabled && isEmpty) return null;

  return (
    <Wrapper ref={connect} id={id}>
      <BlockMaxWidthWrapper>
        <div className="flex justify-between">
          <StyledScheduleTitle>
            <Element
              id="title"
              is={Text}
              text="Schedule"
              element="h2"
              pattern="headingTwo"
            />
          </StyledScheduleTitle>
          <div className="ml-2 flex items-start">
            <FilterWrapper>
              <Popover as="div" className="filter-menu" ref={menuRef}>
                <Filter
                  id="filter-options"
                  label="Filter"
                  totalFiltersSelected={totalActiveFilters}
                  onClick={toggleMenu}
                  isToggled={isMenuOpen}
                />
                {isMenuOpen && (
                  <StyledMenuContent
                    $editing={enabled}
                    role="menu"
                    aria-labelledby="filter-options"
                  >
                    <PartsTagsDisplay
                      onClickPart={handlePartSelection}
                      onClickTag={handleTagSelection}
                      parts={filters.partOptions}
                      selectedParts={selectedParts}
                      selectedTags={selectedTags}
                      tags={filters.tagOptions}
                      onClose={toggleMenu}
                      onClear={clearAllSelections}
                    />
                  </StyledMenuContent>
                )}
              </Popover>
            </FilterWrapper>
            {!isMobile && renderSearchBar()}
          </div>
        </div>
        {isMobile && renderSearchBar()}
        {enabled && isEmpty && <SyncedScheduleSettings type="synced" minimal />}
        {schedulesWithEventTimezone.some((schedule) => schedule.count > 0) ? (
          schedulesWithEventTimezone.map(
            (schedule) =>
              schedule.count > 0 && (
                <div key={schedule.date} className="mb-16">
                  <div className="mb-8 flex items-center">
                    <Typography pattern="headingTwo">
                      {schedule.date}
                    </Typography>
                    {!schedule.isNoFilters && (
                      <Notification
                        aria-label={t("schedule.total-search-results", {
                          total_search: schedule.count,
                        })}
                        role="alert"
                      >
                        {schedule.count}
                      </Notification>
                    )}
                  </div>
                  <StyledSchedulesList>
                    {schedule.items.map((scheduleItem) => (
                      <ScheduleCard
                        key={scheduleItem.id}
                        schedule={scheduleItem}
                      />
                    ))}
                  </StyledSchedulesList>
                </div>
              )
          )
        ) : (
          <EmptyItemWrapper>
            <EmptyItem
              includesSearch={!!searchValue}
              partsCount={selectedParts.length}
              tagsCount={selectedTags.length}
            />
          </EmptyItemWrapper>
        )}
      </BlockMaxWidthWrapper>
    </Wrapper>
  );
}

SyncedDefaultSchedule.craft = {
  custom: {
    displayName: "Synced Schedule",
  },
  related: {
    leftOutlineOptions: TopLevelFloatingOptions,
    settings: () => SyncedScheduleSettings({ type: "synced" }),
  },
};
